import { useEffect, useMemo, useState } from 'react'
import {
  CloseButton,
  Combobox,
  InputBase,
  Loader,
  Text,
  useCombobox,
} from '@mantine/core'
import { useQueryCities } from 'hooks/cities'
import { useAppTranslation } from 'hooks/useTranslation'

const CREATE_OPTION = '$create'

export interface CitySelectValue {
  cityId: number | null
  cityName: string | null
  customCity: string
}

interface CitySelectProps {
  cityId: number | null
  customCity: string
  onChange: (value: CitySelectValue) => void
  label?: string
  error?: React.ReactNode
  withAsterisk?: boolean
  w?: number | string
  mt?: string
}

export const CitySelect = ({
  cityId,
  customCity,
  onChange,
  label,
  error,
  withAsterisk,
  w,
  mt,
}: CitySelectProps) => {
  const { t } = useAppTranslation()
  const { cities, isLoading } = useQueryCities()

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  })

  const selectedCity = useMemo(
    () =>
      cityId !== null
        ? (cities || []).find((city) => city.id === cityId) || null
        : null,
    [cities, cityId]
  )

  const selectedLabel = selectedCity ? selectedCity.name : customCity

  const [search, setSearch] = useState(selectedLabel)

  // Keep the input text in sync when the selection changes externally
  // (e.g. cities finish loading, or an application prefills the form)
  useEffect(() => {
    setSearch(selectedLabel)
  }, [selectedLabel])

  const cityLabel = (city: { name: string; municipality: string }) =>
    city.municipality && city.municipality !== city.name
      ? `${city.name} (${city.municipality})`
      : city.name

  const trimmedSearch = search.trim()

  const filteredCities = useMemo(() => {
    // When the input still shows the current selection, list everything
    if (!trimmedSearch || search === selectedLabel) return cities || []
    const needle = trimmedSearch.toLowerCase()
    return (cities || []).filter((city) =>
      cityLabel(city).toLowerCase().includes(needle)
    )
  }, [cities, search, selectedLabel, trimmedSearch])

  const exactMatch = (cities || []).some(
    (city) => city.name.toLowerCase() === trimmedSearch.toLowerCase()
  )

  const showCreateOption =
    trimmedSearch !== '' && search !== selectedLabel && !exactMatch

  const customCityIsKnown = (cities || []).some(
    (city) => city.name.toLowerCase() === customCity.trim().toLowerCase()
  )

  const handleOptionSubmit = (value: string) => {
    if (value === CREATE_OPTION) {
      setSearch(trimmedSearch)
      onChange({ cityId: null, cityName: null, customCity: trimmedSearch })
    } else {
      const city = (cities || []).find((c) => String(c.id) === value)
      if (city) {
        setSearch(city.name)
        onChange({ cityId: city.id, cityName: city.name, customCity: '' })
      }
    }
    combobox.closeDropdown()
  }

  const handleClear = () => {
    setSearch('')
    onChange({ cityId: null, cityName: null, customCity: '' })
    combobox.closeDropdown()
  }

  return (
    <>
      <Combobox
        store={combobox}
        withinPortal={false}
        onOptionSubmit={handleOptionSubmit}
      >
        <Combobox.Target>
          <InputBase
            component="input"
            type="text"
            label={label}
            withAsterisk={withAsterisk}
            error={error}
            w={w}
            mt={mt}
            value={search}
            rightSection={
              isLoading ? (
                <Loader size="xs" />
              ) : selectedLabel ? (
                <CloseButton
                  size="sm"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={handleClear}
                  aria-label="Clear value"
                />
              ) : (
                <Combobox.Chevron />
              )
            }
            rightSectionPointerEvents={selectedLabel ? 'all' : 'none'}
            onChange={(event) => {
              setSearch(event.currentTarget.value)
              combobox.openDropdown()
              combobox.updateSelectedOptionIndex()
            }}
            onClick={() => combobox.openDropdown()}
            onFocus={() => combobox.openDropdown()}
            onBlur={() => {
              combobox.closeDropdown()
              setSearch(selectedLabel)
            }}
          />
        </Combobox.Target>
        <Combobox.Dropdown>
          <Combobox.Options mah={200} style={{ overflowY: 'auto' }}>
            {filteredCities.map((city) => (
              <Combobox.Option value={String(city.id)} key={city.id}>
                {cityLabel(city)}
              </Combobox.Option>
            ))}
            {showCreateOption && (
              <Combobox.Option value={CREATE_OPTION}>
                {t('cities.use_as_new', { name: trimmedSearch })}
              </Combobox.Option>
            )}
            {filteredCities.length === 0 && !showCreateOption && (
              <Combobox.Empty>{t('cities.no_results')}</Combobox.Empty>
            )}
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
      {cityId === null && customCity.trim() !== '' && !customCityIsKnown && (
        <Text size="xs" c="orange" mt={4} w={w}>
          {t('cities.unreviewed_hint', { name: customCity.trim() })}
        </Text>
      )}
    </>
  )
}
